import { useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

const getAuthHeaders = () => {
  // BFF validates HttpOnly cookie; header is not required.
  return {
    'Content-Type': 'application/json',
  } as HeadersInit;
};

export interface LoginResult {
  status: 'success' | 'unauthorized' | 'not-found' | 'error';
  data: AuthResponse['data'] | null;
  message?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id: string;
      email: string;
    };
    expires: string;
  } | null;
}

export function useAuthApi() {
  // Login API call
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/login`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email, password }),
        credentials: 'include', // for cookies/session
      });

      if (response.status === 201) {
        const data = await response.json();
        return {
          status: 'success',
          data: data.data,
          message: 'Login successful',
        };
      }
      if (response.status === 401) {
        return {
          status: 'unauthorized',
          data: null,
          message: 'Email or password incorrect',
        };
      }
      if (response.status === 404) {
        return { status: 'not-found', data: null, message: 'User not found' };
      }
      // Handle other unexpected errors
      return {
        status: 'error',
        data: null,
        message: 'An unexpected error occurred',
      };
    } catch (error) {
      console.error('Login API error:', error);
      return { status: 'error', data: null, message: 'Login request failed' };
    }
  }, []);

  // Logout API call
  const logout = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Logout request failed:', error);
      return false;
    }
  }, []);

  const signup = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/signup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.status === 201) {
        return { success: true, message: 'Signup successful', data: data.data ?? null };
      }
      return { success: false, message: data.message || 'Signup failed', data: null };
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        message: 'Signup request failed',
        data: null,
      };
    }
  }, []);

  const checkStatus = useCallback(async (): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/v3/status`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data: AuthResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Auth status error:', error);
      return {
        success: false,
        message: 'Auth status request failed',
        data: null,
      };
    }
  }, []);

  return { login, logout, signup, checkStatus };
}
